/**
 * custom-collection.js  —  VELOCE
 *
 * Product card image slider — powered by Swiper.js (loaded globally by the
 * theme in layout/theme.liquid). Behaviour:
 *   - One Swiper instance per .card-media that has multiple images.
 *   - Hover starts autoplay; mouse-leave stops it. Touch devices skip the
 *     hover hook entirely but keep native swipe.
 *   - Custom .card-dots indicators stay in sync with swiper.realIndex.
 *   - Re-initialises on Shopify theme editor section:load events.
 *   - VeloceCart.add() — Ajax add-to-cart with feedback (unchanged).
 */

(function () {
  'use strict';

  /* ── Slider ─────────────────────────────────────────────────────── */

  var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;

  function initSlider(media) {
    if (media._sliderInitialised) return;

    var swiperEl = media.querySelector('.card-media-swiper');
    if (!swiperEl) return;                       // single image → nothing to init
    if (typeof window.Swiper === 'undefined') return;

    media._sliderInitialised = true;

    var dots = Array.from(media.querySelectorAll('[data-dot]'));

    var swiper = new window.Swiper(swiperEl, {
      slidesPerView: 1,
      loop: true,
      speed: 700,
      allowTouchMove: true,
      grabCursor: false,
      preventClicks: true,
      preventClicksPropagation: true,
      // autoplay: {
      //   delay: 1500,
      //   disableOnInteraction: false,
      //   pauseOnMouseEnter: false              // we control via mouseenter/leave
      // }
    });

    // Don't autoplay until the user hovers the card.
    // if (swiper.autoplay && typeof swiper.autoplay.stop === 'function') {
    //   swiper.autoplay.stop();
    // }

    // Keep the custom dot indicators in sync with realIndex (ignores loop dupes).
    swiper.on('slideChange', function () {
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === swiper.realIndex);
      });
    });

    // Dot clicks jump to the corresponding loop slide.
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        swiper.slideToLoop(i);
      });
    });

    // Hover-driven autoplay, bound only on devices that actually support hover.
    // if (canHover) {
    //   media.addEventListener('mouseenter', function () {
    //     if (swiper.autoplay) swiper.autoplay.start();
    //   });
    //   media.addEventListener('mouseleave', function () {
    //     if (swiper.autoplay) swiper.autoplay.stop();
    //   });
    // }
  }

  function initAllSliders(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-slider]').forEach(initSlider);
  }

  /* ── Load More (AJAX pagination) ─────────────────────────────────── */

  function setBtnText(btn, text) {
    var label = btn.querySelector('[data-load-more-text]');
    if (label) label.textContent = text;
    else btn.textContent = text;
  }

  function handleLoadMore(btn) {
    if (btn._isLoading) return;
    var nextUrl = btn.getAttribute('data-next-url');
    if (!nextUrl) return;

    var origText = (btn.querySelector('[data-load-more-text]') || btn).textContent;
    btn._isLoading = true;
    btn.classList.add('is-loading');
    btn.disabled = true;
    setBtnText(btn, 'Loading…');

    fetch(nextUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('load-more-failed');
        return r.text();
      })
      .then(function (html) {
        var doc  = new DOMParser().parseFromString(html, 'text/html');
        var src  = doc.querySelector('[data-load-more-target]');
        var dest = document.querySelector('[data-load-more-target]');
        if (!src || !dest) throw new Error('load-more-missing-target');

        var newCards = Array.from(src.children);
        var frag = document.createDocumentFragment();
        newCards.forEach(function (node, i) {
          if (node.classList) node.classList.add('is-new-card');
          if (node.style)     node.style.animationDelay = (i * 60) + 'ms';
          frag.appendChild(node);
        });
        dest.appendChild(frag);

        // Re-init sliders on the freshly inserted cards only.
        newCards.forEach(function (card) {
          if (card.querySelectorAll) {
            card.querySelectorAll('[data-slider]').forEach(initSlider);
          }
        });

        // After the stagger finishes, drop the marker class + inline delay so
        // the cards settle into normal state.
        var totalDur = 600 + newCards.length * 60 + 100;
        setTimeout(function () {
          newCards.forEach(function (card) {
            if (card.classList) card.classList.remove('is-new-card');
            if (card.style)     card.style.animationDelay = '';
          });
        }, totalDur);

        // Update the button with the next page's URL, or finalise it.
        var nextBtn = doc.querySelector('[data-load-more-btn]');
        if (nextBtn) {
          var newUrl = nextBtn.getAttribute('data-next-url');
          if (newUrl) {
            btn.setAttribute('data-next-url', newUrl);
            btn.classList.remove('is-loading');
            btn.disabled = false;
            btn._isLoading = false;
            setBtnText(btn, origText);
            return;
          }
        }

        // No more pages — lock the button into the done state.
        btn.classList.remove('is-loading');
        btn.classList.add('is-done');
        btn.removeAttribute('data-next-url');
        setBtnText(btn, 'No More Products');
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        btn._isLoading = false;
        setBtnText(btn, 'Retry');
        setTimeout(function () { setBtnText(btn, origText); }, 2200);
      });
  }

  function initLoadMore(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-load-more-btn]').forEach(function (btn) {
      if (btn._loadMoreBound) return;
      btn._loadMoreBound = true;
      var trigger = btn.closest('[data-load-more-wrap]') || btn;
      btn.hidden = true;
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) handleLoadMore(btn);
      }, { rootMargin: '200px 0px' });
      observer.observe(trigger);
    });
  }

  /* ── Cart ────────────────────────────────────────────────────────── */

  // Resolve the theme's existing Ajax cart drawer component. The theme ships
  // <cart-notification> (theme.js); we also accept <cart-drawer> for parity
  // with the theme's own lookups (main-product.js). We do NOT create a new
  // cart system \u2014 we drive this one.
  function getThemeCart() {
    return (
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer')
    );
  }

  // Refresh + open the theme's drawer using ITS OWN methods, mirroring the
  // theme's native add flow (CartNotification.addGiftwrapClick in theme.js):
  //   1. update header cart counts/totals from the live cart,
  //   2. swap in the freshly-rendered minicart markup returned with the add,
  //   3. re-bind drawer handlers (cartAction) and open() the drawer.
  function refreshAndOpenCart(cart, parsed) {
    if (!cart) {
      // No theme drawer on this page (shouldn't happen on a normal collection
      // page). Notify any listeners; counts will catch up on next navigation.
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      return;
    }

    // 1) Header counts + totals (best-effort; never blocks the UI).
    fetch('/cart.json')
      .then(function (res) { return res.json(); })
      .then(function (c) {
        if (c.item_count == null) return;
        document.querySelectorAll('.cart-count').forEach(function (el) {
          if (el.classList.contains('cart-count-drawer')) {
            el.innerHTML = '(' + c.item_count + ')';
          } else {
            el.innerHTML = c.item_count > 100 ? '~' : c.item_count;
          }
        });
        var totalEl = document.querySelector('header-total-price');
        if (totalEl && typeof totalEl.updateTotal === 'function') totalEl.updateTotal(c);
        var freeShip = document.querySelector('free-ship-progress-bar');
        if (freeShip && typeof freeShip.init === 'function') freeShip.init(c.items_subtotal_price);
      })
      .catch(function () { /* counts are best-effort */ });

    // 2) Replace the minicart contents with the section HTML the add returned.
    if (parsed && parsed.sections && typeof cart.getSectionsToRender === 'function') {
      cart.getSectionsToRender().forEach(function (section) {
        var elementToReplace = document.getElementById(section.id);
        var sectionHTML = parsed.sections[section.id];
        if (!elementToReplace || !sectionHTML) return;
        var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
        var fresh = doc.querySelector('#minicart-form');
        if (fresh) elementToReplace.innerHTML = fresh.innerHTML;
      });
    }

    // 3) Re-bind handlers after the re-render, then open the drawer.
    if (typeof cart.cartAction === 'function') cart.cartAction();
    if (typeof cart.open === 'function') cart.open();
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  window.VeloceCart = {
    add: function (variantId, btn) {
      if (!variantId || btn.disabled) return;

      var cart = getThemeCart();
      var orig = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span>Adding\u2026</span>';

      // Ask Shopify to render the drawer's section(s) alongside the add so the
      // response carries a fresh minicart we can swap straight in.
      var body = { id: Number(variantId), quantity: 1 };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      })
      .then(function (r) {
        // Read the body regardless of status so we can surface Shopify's error
        // description (e.g. "All N are in your cart") on a 422.
        return r.text().then(function (text) {
          var parsed = null;
          try { parsed = JSON.parse(text); } catch (e) { /* non-JSON */ }
          return { ok: r.ok, parsed: parsed };
        });
      })
      .then(function (res) {
        // Only a genuine success (2xx, parseable, no error status) is allowed
        // to flip the button to "Added". A 422 / error body throws below so we
        // never show a fake success.
        if (!res.ok || !res.parsed || res.parsed.status) {
          throw new Error(
            res.parsed && res.parsed.description ? res.parsed.description : 'cart-add-failed'
          );
        }

        refreshAndOpenCart(cart, res.parsed);

        btn.innerHTML = '<span>Added \u2713</span>';
        setTimeout(function () {
          btn.innerHTML = orig;
          btn.disabled = false;
        }, 1800);
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[VeloceCart] add to cart failed:', (err && err.message) || err);
        }
        btn.innerHTML = '<span>Error \u2014 retry</span>';
        setTimeout(function () {
          btn.innerHTML = orig;
          btn.disabled = false;
        }, 2200);
      });
    }
  };

  /* ── Shopify theme editor live-reload ────────────────────────────── */
  // Re-init sliders and load-more button when a section is reloaded in the customizer
  document.addEventListener('shopify:section:load', function (e) {
    initAllSliders(e.target);
    initLoadMore(e.target);
  });

  /* ── Public API ──────────────────────────────────────────────────── */
  window.VeloceCollection = {
    initSliders:  initAllSliders,
    initLoadMore: initLoadMore
  };

  /* ── Boot ────────────────────────────────────────────────────────── */
  function boot() {
    initAllSliders();
    initLoadMore();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

}());
